import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Camera, Upload, Sparkles, CheckCircle2, AlertTriangle, Receipt, FileText,
  Edit3, Layers, ArrowRight, X, Loader2, Building2, Plus, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { BulkQuickCaptureDialog } from '@/components/BulkQuickCapture';

const COST_TYPES = [
  { id: 'paid', label: 'Paid', desc: 'Already paid in full', color: 'green', Icon: CheckCircle2 },
  { id: 'partial', label: 'Partial Payment', desc: 'Some paid, more due', color: 'blue', Icon: Layers },
  { id: 'invoice', label: 'Invoice / Bill Due', desc: 'Need to pay this', color: 'amber', Icon: FileText },
  { id: 'estimate', label: 'Estimate / Quote', desc: 'Not committed yet', color: 'slate', Icon: Edit3 },
];

const CONSTRUCTION_PHASES = [
  { number: 1, name: 'Planning and Permitting' },
  { number: 2, name: 'Site Preparation and Foundation' },
  { number: 3, name: 'Framing' },
  { number: 4, name: 'Exterior and Systems (Rough-in)' },
  { number: 5, name: 'Insulation and Drywall' },
  { number: 6, name: 'Interior Finishes' },
  { number: 7, name: 'Fixtures and Exterior Finishing' },
  { number: 8, name: 'Final Walkthrough and Handover' },
];

const NEW_BADGE_KEY = 'quick-capture-new-seen';
const NEW_BADGE_FIRST_SEEN = 'quick-capture-first-seen-at';

// Show NEW badge for 30 days from first sight, OR until first capture
const useShowNewBadge = () => {
  const [show, setShow] = useState(true);
  useEffect(() => {
    const seenAt = localStorage.getItem(NEW_BADGE_FIRST_SEEN);
    const dismissed = localStorage.getItem(NEW_BADGE_KEY) === 'true';
    if (dismissed) {
      setShow(false);
      return;
    }
    if (!seenAt) {
      localStorage.setItem(NEW_BADGE_FIRST_SEEN, Date.now().toString());
    } else {
      const ageDays = (Date.now() - parseInt(seenAt, 10)) / (1000 * 60 * 60 * 24);
      if (ageDays > 30) {
        localStorage.setItem(NEW_BADGE_KEY, 'true');
        setShow(false);
      }
    }
  }, []);
  const dismiss = () => {
    localStorage.setItem(NEW_BADGE_KEY, 'true');
    setShow(false);
  };
  return [show, dismiss];
};

const NewBadge = ({ className = '' }) => (
  <span
    className={`absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full animate-pulse z-10 ${className}`}
    data-testid="quick-capture-new-badge"
  >
    NEW
  </span>
);

// =====================
// The DIALOG (full flow)
// =====================
const QuickCaptureDialog = ({ open, onOpenChange, currentProjectId, onCaptureComplete, onSwitchToBulk }) => {
  const [step, setStep] = useState('upload'); // upload | extracting | duplicate | confirm | filing
  const [extractResult, setExtractResult] = useState(null);
  const [duplicateAcknowledged, setDuplicateAcknowledged] = useState(false);
  const [projects, setProjects] = useState([]);
  const [phases, setPhases] = useState([]);
  const [form, setForm] = useState({});
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const navigate = useNavigate();

  const userId = (() => {
    try { return JSON.parse(localStorage.getItem('user'))?.id; } catch { return null; }
  })();

  // Load user's projects on open
  useEffect(() => {
    if (!open) return;
    setStep('upload');
    setExtractResult(null);
    setDuplicateAcknowledged(false);
    setForm({});
    apiService.getProjects(userId)
      .then((res) => setProjects((res.data || []).filter((p) => p.status !== 'completed')))
      .catch(() => setProjects([]));
  }, [open]);

  const loadPhases = useCallback(async (projectId) => {
    if (!projectId) return;
    try {
      const res = await apiService.getPhases(projectId);
      setPhases(res.data || []);
    } catch {
      setPhases([]);
    }
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    if (!userId) {
      toast.error('Please log in first');
      return;
    }
    setStep('extracting');
    try {
      const res = await apiService.quickCaptureExtract(userId, file, currentProjectId);
      const data = res.data;
      setExtractResult(data);

      const projectId = currentProjectId || data.project_guess?.project_id || null;
      const phaseId = data.phase_suggestion?.match === 'existing' ? data.phase_suggestion.phase_id : null;
      const isNewPhase = data.phase_suggestion?.match === 'new';

      setForm({
        project_id: projectId,
        phase_mode: isNewPhase ? 'new' : 'existing',
        phase_id: phaseId,
        new_phase_name: data.phase_suggestion?.phase_name || data.extracted?.vendor || '',
        new_phase_construction_number: data.phase_suggestion?.construction_phase_number || 1,
        new_phase_type: data.phase_suggestion?.suggested_phase_type || data.extracted?.suggested_category || 'material',
        cost_type: data.extracted?.suggested_cost_type || 'paid',
        amount: data.extracted?.amount ?? 0,
        paid_amount: data.extracted?.amount ?? 0,
        vendor: data.extracted?.vendor || '',
        receipt_date: data.extracted?.date || '',
        description: data.extracted?.description || '',
      });

      if (projectId) loadPhases(projectId);
      // If a duplicate was detected, route to the blocking duplicate step.
      // Otherwise go straight to the confirm form.
      if (data.duplicate) {
        setStep('duplicate');
      } else {
        setStep('confirm');
      }
    } catch (err) {
      console.error('Quick capture extract failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to extract receipt');
      setStep('upload');
    }
  };

  const handleProjectChange = (projectId) => {
    setForm((p) => ({ ...p, project_id: projectId, phase_id: null }));
    loadPhases(projectId);
  };

  const handleSubmit = async () => {
    if (!form.project_id) {
      toast.error('Please select a project');
      return;
    }
    if (form.phase_mode === 'existing' && !form.phase_id) {
      toast.error('Please select a phase or switch to "Create new"');
      return;
    }
    if (!form.amount || form.amount <= 0) {
      toast.error('Amount is required');
      return;
    }

    setStep('filing');
    try {
      const payload = {
        user_id: userId,
        project_id: form.project_id,
        phase_id: form.phase_mode === 'existing' ? form.phase_id : null,
        new_phase: form.phase_mode === 'new' ? {
          name: form.new_phase_name,
          construction_phase_number: form.new_phase_construction_number,
          phase_type: form.new_phase_type,
        } : null,
        cost_type: form.cost_type,
        amount: parseFloat(form.amount),
        paid_amount: form.cost_type === 'partial' ? parseFloat(form.paid_amount || 0) : null,
        file_id: extractResult.file_id,
        file_hash: extractResult.file_hash,
        file_name: extractResult.file_name,
        vendor: form.vendor,
        vendor_aliases: extractResult.extracted?.vendor_aliases || [],
        receipt_date: form.receipt_date,
        description: form.description,
        duplicate_acknowledged: duplicateAcknowledged,
      };
      const res = await apiService.quickCaptureFile(payload);
      toast.success(res.data?.message || 'Filed successfully');
      onOpenChange(false);
      if (onCaptureComplete) onCaptureComplete(res.data);
    } catch (err) {
      console.error('Quick capture file failed:', err);
      // Backend safety net flagged a duplicate the UI hadn't seen yet
      if (err.response?.status === 409 && err.response?.data?.detail?.duplicate) {
        const dup = err.response.data.detail.duplicate;
        setExtractResult((prev) => prev ? { ...prev, duplicate: dup } : prev);
        setStep('duplicate');
        toast.warning('Possible duplicate detected — please confirm before filing');
        return;
      }
      toast.error(err.response?.data?.detail || 'Failed to file');
      setStep('confirm');
    }
  };

  const handleSkipDuplicate = async () => {
    if (extractResult?.file_id) {
      apiService.quickCaptureDiscard(extractResult.file_id).catch(() => {});
    }
    toast.info('Skipped — already filed');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="quick-capture-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-amber-600" /> Quick Capture
          </DialogTitle>
          <DialogDescription>
            Snap or upload a receipt, invoice, or estimate. AI extracts the details and files it for you.
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4 py-4">
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" hidden onChange={(e) => handleFile(e.target.files?.[0])} data-testid="quick-capture-file-input" />
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => handleFile(e.target.files?.[0])} />

            {/* Primary: single receipt upload */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-2 border-dashed border-amber-300 bg-amber-50 hover:bg-amber-100 rounded-xl p-8 text-center transition-colors"
              data-testid="quick-capture-upload-zone"
            >
              <Upload className="w-12 h-12 mx-auto text-amber-600 mb-2" />
              <div className="font-semibold text-amber-900">Upload one receipt</div>
              <div className="text-xs text-amber-700">JPG, PNG, HEIC, or PDF — up to 10MB</div>
            </button>

            <div className="md:hidden">
              <Button variant="outline" className="w-full" onClick={() => cameraInputRef.current?.click()} data-testid="quick-capture-take-photo">
                <Camera className="w-4 h-4 mr-2" /> Take a photo
              </Button>
            </div>

            {/* Secondary: bulk upload */}
            {onSwitchToBulk && (
              <>
                <div className="flex items-center gap-3 text-xs text-slate-400 uppercase tracking-wider">
                  <div className="flex-1 h-px bg-slate-200" />
                  or
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <button
                  type="button"
                  onClick={onSwitchToBulk}
                  className="w-full border border-slate-200 bg-white hover:bg-slate-50 rounded-xl p-4 text-left flex items-center gap-3 transition-colors group"
                  data-testid="quick-capture-bulk-switch"
                >
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white flex-shrink-0">
                    <Layers className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 text-sm">Bulk upload</span>
                      <span className="text-[9px] bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded-full font-bold">NEW</span>
                    </div>
                    <div className="text-xs text-slate-500">Drag &amp; drop multiple receipts at once — review row by row</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </button>
              </>
            )}
          </div>
        )}

        {step === 'extracting' && (
          <div className="py-12 text-center space-y-3">
            <Loader2 className="w-12 h-12 mx-auto text-amber-600 animate-spin" />
            <div className="font-semibold text-slate-900">AI is reading your document…</div>
            <div className="text-xs text-slate-500">Extracting vendor, amount, date, and matching to a phase</div>
          </div>
        )}

        {step === 'duplicate' && extractResult?.duplicate && (() => {
          const d = extractResult.duplicate;
          const ex = d.existing || {};
          const isPossibleMatch = d.match_type === 'amount_date'; // lower confidence
          const newFileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${extractResult.file_id}`;
          const existingFileUrl = ex.file_id
            ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${ex.file_id}`
            : null;
          return (
            <div className="space-y-4 py-2" data-testid="quick-capture-duplicate-step">
              {/* Soft notice — tone shifts based on match confidence */}
              <div className={`rounded-lg p-3 flex items-start gap-2 border ${
                isPossibleMatch
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-blue-50 border-blue-200'
              }`}>
                {isPossibleMatch ? (
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                )}
                <div className={`text-sm ${isPossibleMatch ? 'text-amber-900' : 'text-blue-900'}`}>
                  <strong>
                    {isPossibleMatch ? 'Possible duplicate.' : "You've already filed this receipt."}
                  </strong>{' '}
                  <span className={isPossibleMatch ? 'text-amber-700' : 'text-blue-700'}>
                    {d.match_strength || 'Same vendor, amount and date.'}
                  </span>
                </div>
              </div>

              {/* Side-by-side comparison cards with thumbnails */}
              <div className="grid grid-cols-2 gap-3">
                {/* New upload */}
                <div className="bg-white border-2 border-slate-200 rounded-xl overflow-hidden">
                  <div className="aspect-[3/4] bg-slate-100 flex items-center justify-center overflow-hidden">
                    <img
                      src={newFileUrl}
                      alt="New upload"
                      className="object-contain w-full h-full"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div className="p-3 border-t bg-slate-50">
                    <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">📷 You just uploaded</div>
                    <div className="text-sm font-semibold truncate">{extractResult.extracted.vendor || 'Unknown'}</div>
                    <div className="text-xs text-slate-600">${Number(extractResult.extracted.amount || 0).toFixed(2)} · {extractResult.extracted.date || '—'}</div>
                  </div>
                </div>

                {/* Existing */}
                <div className="bg-white border-2 border-emerald-300 rounded-xl overflow-hidden ring-2 ring-emerald-100">
                  <div className="aspect-[3/4] bg-slate-100 flex items-center justify-center overflow-hidden relative">
                    {existingFileUrl ? (
                      <>
                        <img
                          src={existingFileUrl}
                          alt="Already filed"
                          className="object-contain w-full h-full"
                          onError={(e) => {
                            // React-safe fallback: hide the broken img, sibling div takes over
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <div className="text-slate-400 text-xs absolute inset-0 flex items-center justify-center pointer-events-none -z-10">No preview</div>
                      </>
                    ) : (
                      <div className="text-slate-400 text-xs">No preview</div>
                    )}
                  </div>
                  <div className="p-3 border-t bg-emerald-50">
                    <div className="text-[10px] font-bold uppercase text-emerald-700 mb-1 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Already filed
                    </div>
                    <div className="text-sm font-semibold truncate">{ex.vendor || 'Unknown'}</div>
                    <div className="text-xs text-slate-600">${Number(ex.amount || 0).toFixed(2)} · {ex.date || ex.receipt_date || '—'}</div>
                    {ex.location_breadcrumb ? (
                      <div className="text-xs text-emerald-800 mt-2 font-medium leading-snug" data-testid="quick-capture-breadcrumb">
                        📍 {ex.location_breadcrumb}
                      </div>
                    ) : ex.phase_name ? (
                      <div className="text-xs text-emerald-800 mt-2 font-medium">📍 {ex.phase_name}</div>
                    ) : null}
                    {ex.project_name && ex.location_breadcrumb && (
                      <div className="text-[10px] text-slate-500 mt-0.5 truncate">{ex.project_name}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Primary actions */}
              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                  onClick={handleSkipDuplicate}
                  data-testid="quick-capture-skip-duplicate"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Got it — skip
                </Button>
                {existingFileUrl && (
                  <Button
                    variant="outline"
                    onClick={() => window.open(existingFileUrl, '_blank', 'noopener,noreferrer')}
                    data-testid="quick-capture-view-receipt"
                  >
                    <Eye className="w-4 h-4 mr-2" /> View existing receipt
                  </Button>
                )}
                {!existingFileUrl && ex.project_id && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      onOpenChange(false);
                      const hash = ex.phase_id ? `#phase-${ex.phase_id}` : '';
                      navigate(`/project/${ex.project_id}${hash}`);
                    }}
                    data-testid="quick-capture-view-existing"
                  >
                    <Eye className="w-4 h-4 mr-2" /> Go to phase
                  </Button>
                )}
              </div>

              {/* Tertiary "add it anyway" link */}
              <div className="text-center pt-1">
                <button
                  className="text-xs text-slate-500 hover:text-slate-700 underline"
                  onClick={() => { setDuplicateAcknowledged(true); setStep('confirm'); }}
                  data-testid="quick-capture-add-anyway"
                >
                  This is actually different — add it anyway
                </button>
              </div>
            </div>
          );
        })()}

        {step === 'confirm' && extractResult && (
          <div className="space-y-4">
            {/* AI extraction summary */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-700" />
              <span className="text-emerald-900">AI extracted: <strong>{extractResult.extracted.vendor || 'Unknown'}</strong> — <strong>${Number(extractResult.extracted.amount || 0).toFixed(2)}</strong></span>
            </div>

            {/* Project picker */}
            <div>
              <Label className="text-sm font-semibold">Project</Label>
              {extractResult.project_guess && !currentProjectId && (
                <div className="text-xs text-slate-500 mb-1">
                  AI guessed: <strong>{extractResult.project_guess.project_name}</strong> ({Math.round((extractResult.project_guess.confidence || 0) * 100)}%) — {extractResult.project_guess.reason}
                </div>
              )}
              <Select value={form.project_id || ''} onValueChange={handleProjectChange}>
                <SelectTrigger data-testid="quick-capture-project-select">
                  <SelectValue placeholder="Pick a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} {p.address ? `· ${p.address}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Phase mode */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setForm((p) => ({ ...p, phase_mode: 'existing' }))}
                className={`rounded-lg border-2 p-3 text-left transition-all ${form.phase_mode === 'existing' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'}`}
                data-testid="quick-capture-phase-existing"
              >
                <div className="flex items-center gap-1.5 font-semibold text-sm"><Layers className="w-4 h-4" /> Add to existing</div>
                <div className="text-xs text-slate-500 mt-0.5">Pick from current phases</div>
              </button>
              <button
                onClick={() => setForm((p) => ({ ...p, phase_mode: 'new' }))}
                className={`rounded-lg border-2 p-3 text-left transition-all ${form.phase_mode === 'new' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'}`}
                data-testid="quick-capture-phase-new"
              >
                <div className="flex items-center gap-1.5 font-semibold text-sm"><Plus className="w-4 h-4" /> Create new phase</div>
                <div className="text-xs text-slate-500 mt-0.5">No matching phase exists</div>
              </button>
            </div>

            {form.phase_mode === 'existing' && (
              <div>
                <Label className="text-sm">Phase</Label>
                {extractResult.phase_suggestion?.match === 'existing' && (
                  <div className="text-xs text-slate-500 mb-1">AI suggests: <strong>{extractResult.phase_suggestion.phase_name}</strong> ({Math.round((extractResult.phase_suggestion.confidence || 0) * 100)}%) — {extractResult.phase_suggestion.reason}</div>
                )}
                <Select value={form.phase_id || ''} onValueChange={(v) => setForm((p) => ({ ...p, phase_id: v }))}>
                  <SelectTrigger data-testid="quick-capture-phase-select"><SelectValue placeholder="Pick a phase" /></SelectTrigger>
                  <SelectContent>
                    {phases.map((ph) => (
                      <SelectItem key={ph.id} value={ph.id}>{ph.name} ({ph.phase_type})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {form.phase_mode === 'new' && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-3">
                <div>
                  <Label className="text-xs">New phase name</Label>
                  <Input value={form.new_phase_name || ''} onChange={(e) => setForm((p) => ({ ...p, new_phase_name: e.target.value }))} data-testid="quick-capture-new-phase-name" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Construction phase</Label>
                    <Select value={String(form.new_phase_construction_number || 1)} onValueChange={(v) => setForm((p) => ({ ...p, new_phase_construction_number: parseInt(v, 10) }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CONSTRUCTION_PHASES.map((cp) => (
                          <SelectItem key={cp.number} value={String(cp.number)}>{cp.number}: {cp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Type</Label>
                    <Select value={form.new_phase_type || 'material'} onValueChange={(v) => setForm((p) => ({ ...p, new_phase_type: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="material">Material</SelectItem>
                        <SelectItem value="labor">Labor</SelectItem>
                        <SelectItem value="soft_cost">Soft Cost</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Cost type chooser */}
            <div>
              <Label className="text-sm font-semibold">Cost type</Label>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {COST_TYPES.map((ct) => {
                  const isActive = form.cost_type === ct.id;
                  const Icon = ct.Icon;
                  return (
                    <button
                      key={ct.id}
                      onClick={() => setForm((p) => ({ ...p, cost_type: ct.id }))}
                      className={`text-left rounded-lg border-2 p-2.5 transition-all ${isActive ? 'border-amber-500 bg-amber-50' : 'border-slate-200 bg-white'}`}
                      data-testid={`quick-capture-cost-type-${ct.id}`}
                    >
                      <div className="flex items-center gap-1.5 font-semibold text-sm"><Icon className="w-4 h-4" /> {ct.label}</div>
                      <div className="text-[10px] text-slate-500">{ct.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Editable extracted fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Vendor</Label>
                <Input value={form.vendor || ''} onChange={(e) => setForm((p) => ({ ...p, vendor: e.target.value }))} data-testid="quick-capture-vendor" />
              </div>
              <div>
                <Label className="text-xs">Total amount</Label>
                <Input type="number" value={form.amount || ''} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} data-testid="quick-capture-amount" />
              </div>
              {form.cost_type === 'partial' && (
                <div>
                  <Label className="text-xs">Paid so far</Label>
                  <Input type="number" value={form.paid_amount || ''} onChange={(e) => setForm((p) => ({ ...p, paid_amount: e.target.value }))} data-testid="quick-capture-paid-amount" />
                </div>
              )}
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={form.receipt_date || ''} onChange={(e) => setForm((p) => ({ ...p, receipt_date: e.target.value }))} data-testid="quick-capture-date" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea rows={2} value={form.description || ''} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} data-testid="quick-capture-description" />
            </div>
          </div>
        )}

        {step === 'filing' && (
          <div className="py-12 text-center space-y-3">
            <Loader2 className="w-10 h-10 mx-auto text-amber-600 animate-spin" />
            <div className="font-semibold text-slate-900">Filing your receipt…</div>
          </div>
        )}

        {step === 'confirm' && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="quick-capture-cancel">Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleSubmit} data-testid="quick-capture-submit">
              File Receipt <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

// =====================
// Public-facing entry components
// =====================
export const QuickCaptureFAB = () => {
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [showNew, dismissNew] = useShowNewBadge();
  const location = useLocation();
  const navigate = useNavigate();

  // Hide on auth/landing/preview routes
  const hidden = /^(\/$|\/login|\/signup|\/admin-login|\/auth\/|\/preview|\/.*-preview$|\/contractor-compliance|\/contractor-portal|\/quote-portal|\/draw-portal|\/upload-portal|\/email-verified|\/forgot-password|\/reset-password|\/complete-signup|\/affiliate)/i.test(location.pathname);
  if (hidden) return null;

  // Detect current project from URL
  const projectMatch = location.pathname.match(/\/projects?\/([a-f0-9-]+)/i);
  const currentProjectId = projectMatch ? projectMatch[1] : null;

  const handleOpen = () => {
    if (showNew) dismissNew();
    setOpen(true);
  };

  const handleSwitchToBulk = () => {
    setOpen(false);
    setBulkOpen(true);
  };

  const handleComplete = () => {
    if (currentProjectId) navigate(0);
  };

  return (
    <>
      {/* Single FAB — the dialog itself offers Single vs Bulk choice */}
      <div className="fixed bottom-24 right-5 z-40 group" data-testid="quick-capture-fab-wrapper">
        <button
          onClick={handleOpen}
          className="relative w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 shadow-2xl hover:shadow-amber-500/50 flex items-center justify-center text-white hover:scale-105 active:scale-95 transition-all"
          aria-label="Quick Capture - snap or upload a receipt"
          data-testid="quick-capture-fab"
        >
          <Camera className="w-6 h-6" />
          {showNew && <NewBadge />}
        </button>
        <div className="hidden md:block absolute bottom-16 right-0 bg-slate-900 text-white text-xs px-3 py-2 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
          <div className="font-bold">Quick Capture</div>
          <div className="text-slate-300 text-[11px]">Snap a receipt — AI files it</div>
          <div className="absolute right-5 -bottom-1 w-2 h-2 bg-slate-900 rotate-45" />
        </div>
      </div>

      <QuickCaptureDialog
        open={open}
        onOpenChange={setOpen}
        currentProjectId={currentProjectId}
        onCaptureComplete={handleComplete}
        onSwitchToBulk={handleSwitchToBulk}
      />
      <BulkQuickCaptureDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onComplete={() => navigate(0)}
      />
    </>
  );
};

// Top-bar inline button - desktop prominent variant
export const QuickCaptureTopButton = ({ projectId }) => {
  const [open, setOpen] = useState(false);
  const [showNew, dismissNew] = useShowNewBadge();
  const navigate = useNavigate();

  const handleOpen = () => {
    if (showNew) dismissNew();
    setOpen(true);
  };

  return (
    <>
      <div className="relative inline-block">
        <Button
          onClick={handleOpen}
          className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-md font-semibold"
          data-testid="quick-capture-top-button"
        >
          <Camera className="w-4 h-4 mr-1.5" /> Quick Capture
        </Button>
        {showNew && <NewBadge />}
      </div>
      <QuickCaptureDialog
        open={open}
        onOpenChange={setOpen}
        currentProjectId={projectId}
        onCaptureComplete={() => navigate(0)}
      />
    </>
  );
};

export default QuickCaptureFAB;
